import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, CircleAlert } from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { apiPost } from '../lib/api';
import { LogoWordmark } from '../components/Logo';
import { useT } from '../i18n';
import type { ZerodhaConnection } from '../lib/types';

type Status = 'working' | 'done' | 'error';

// Zerodha Kite Connect redirects back here with ?request_token=...&state=...
// We exchange the request token server-side, then send the user to Invest.
export default function KiteCallback() {
  const t = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, loading, csrfToken } = useAuth();
  const [status, setStatus] = useState<Status>('working');
  const [message, setMessage] = useState(t('Completing your Zerodha connection…'));
  const fired = useRef(false);

  useEffect(() => {
    if (loading || fired.current) return;
    if (!session || !csrfToken) {
      setStatus('error');
      setMessage(t('Please sign in first, then reconnect Zerodha from the Connect tab.'));
      return;
    }
    const requestToken = params.get('request_token');
    const state = params.get('state');
    if (!requestToken || !state) {
      setStatus('error');
      setMessage(t('The redirect from Kite was missing details. Please start the connection again from the Connect tab.'));
      return;
    }
    fired.current = true;
    apiPost<{ connection: ZerodhaConnection }>('/api/zerodha/connect/callback', { requestToken, state, csrfToken })
      .then(() => {
        setStatus('done');
        setMessage(t('Zerodha connected. Taking you to Connect…'));
        setTimeout(() => navigate('/app/aggregation', { replace: true }), 1200);
      })
      .catch((err: Error) => {
        setStatus('error');
        setMessage(err.message || t('We could not complete the connection. Please try again from the Connect tab.'));
      });
  }, [loading, session, csrfToken, params, navigate]);

  return (
    <div className="flex min-h-dvh flex-col bg-surface-sunken dark:bg-surface-dark">
      <header className="px-6 py-5">
        <Link to="/" aria-label={t('Sansara home')}>
          <LogoWordmark />
        </Link>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pb-24">
        <div className="card w-full max-w-md p-8 text-center">
          {status === 'working' ? <div className="skeleton mx-auto h-10 w-10 rounded-full" /> : null}
          {status === 'done' ? <CheckCircle2 size={40} className="mx-auto text-gain dark:text-gain-dark" /> : null}
          {status === 'error' ? <CircleAlert size={40} className="mx-auto text-loss dark:text-loss-dark" /> : null}
          <h1 className="mt-4 font-display text-xl font-bold">
            {status === 'done' ? t('Connected') : status === 'error' ? t('Connection issue') : t('Connecting Zerodha')}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft dark:text-slate-400">{message}</p>
          {status === 'error' ? (
            <Link to="/app/aggregation" className="btn-primary mt-6 inline-flex">
              {t('Go to Connect')}
            </Link>
          ) : null}
        </div>
      </main>
    </div>
  );
}
